import React from "react";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";

import { uploadLearningFile } from "./swipeDeckHelpers";
import { MediaSuggestionBanner } from "./SwipeCardMediaSuggestion";
import MediaLibraryModal from "./MediaLibraryModal";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";

const QUESTION_MAX = 120;
// Generous safety-net cap, matching the AI-generation backstop in
// learning_swipe_deck.py. A tight 60-char limit silently truncated real
// sentence-length answer choices; 200 fits multi-line choices comfortably.
const CHOICE_MAX = 200;
const MIN_CHOICES = 2;
const MAX_CHOICES = 4;

export function SwipeCardTriviaEditor({ card, onChange, companyId }) {
  const content = card.content_json || {};
  const question = content.question || "";
  const choices =
    Array.isArray(content.choices) && content.choices.length >= MIN_CHOICES
      ? content.choices
      : [
          { text: "", is_correct: false },
          { text: "", is_correct: false },
        ];
  const explanation = content.explanation || "";
  const shuffleChoices = content.shuffle_choices !== false; // default true
  const questionImageUrl = card.media_url || null;

  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState(null);
  const [libraryOpen, setLibraryOpen] = React.useState(false);

  const updateContent = (patch) => {
    onChange({ ...card, content_json: { ...content, ...patch } });
  };

  const updateChoice = (index, patch) => {
    const next = choices.map((c, i) => (i === index ? { ...c, ...patch } : c));
    updateContent({ choices: next });
  };

  const addChoice = () => {
    if (choices.length >= MAX_CHOICES) return;
    updateContent({
      choices: [...choices, { text: "", is_correct: false }],
    });
  };

  const removeChoice = (index) => {
    if (choices.length <= MIN_CHOICES) return;
    updateContent({ choices: choices.filter((_, i) => i !== index) });
  };

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setUploadError("Please choose an image file.");
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const { uuid, url } = await uploadLearningFile(companyId, file);
      onChange({ ...card, media_file_uuid: uuid, media_url: url });
    } catch (err) {
      setUploadError(err?.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleClearImage = () => {
    onChange({ ...card, media_file_uuid: null, media_url: null });
  };

  const correctCount = choices.filter((c) => c.is_correct).length;
  const noCorrectAnswerWarning = correctCount === 0 && choices.length > 0;

  return (
    <div className="flex flex-col gap-5 p-5">
      {/* Question */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide">
            Question
          </label>
          <span
            className={[
              "text-[11px] tabular-nums",
              question.length > QUESTION_MAX
                ? "text-red-600 font-semibold"
                : "text-slate-400",
            ].join(" ")}
          >
            {question.length}/{QUESTION_MAX}
          </span>
        </div>
        <textarea
          value={question}
          onChange={(e) => updateContent({ question: e.target.value })}
          rows={2}
          maxLength={QUESTION_MAX}
          placeholder="What do you want them to know?"
          className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-100 text-sm resize-none"
        />
      </div>

      {/* Optional question image */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
          Question image (optional)
        </label>
        <MediaSuggestionBanner card={card} />
        {questionImageUrl ? (
          <div className="relative rounded-lg overflow-hidden border border-slate-200 bg-black">
            <img
              src={questionImageUrl}
              alt=""
              className="w-full h-28 object-cover"
            />
            <button
              type="button"
              onClick={handleClearImage}
              className="absolute top-2 right-2 px-2 py-1 rounded-md bg-black/70 text-white text-xs hover:bg-black/90"
            >
              Remove
            </button>
          </div>
        ) : (
          <label className="flex items-center justify-center h-20 rounded-lg border-2 border-dashed border-slate-300 hover:border-violet-500 cursor-pointer transition-colors text-xs text-slate-500">
            {uploading ? "Uploading…" : "Upload an image to go with the question"}
            <input
              type="file"
              accept="image/*"
              onChange={handleImagePick}
              disabled={uploading}
              className="hidden"
            />
          </label>
        )}
        <button
          type="button"
          onClick={() => setLibraryOpen(true)}
          disabled={uploading}
          className="mt-2 w-full px-3 py-1.5 text-xs font-medium text-violet-700 border border-violet-200 rounded-lg hover:bg-violet-50 transition-colors inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
        >
          <CollectionsOutlinedIcon style={{ fontSize: 14 }} />
          {questionImageUrl ? "Replace from library" : "Choose from library"}
        </button>
        {uploadError && (
          <div className="mt-1 text-xs text-red-600">{uploadError}</div>
        )}
        <MediaLibraryModal
          open={libraryOpen}
          onClose={() => setLibraryOpen(false)}
          companyId={companyId}
          onSelect={({ uuid, url }) =>
            onChange({ ...card, media_file_uuid: uuid, media_url: url })
          }
        />
      </div>

      {/* Choices */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide">
            Answers ({choices.length}/{MAX_CHOICES})
          </label>
          <button
            type="button"
            onClick={addChoice}
            disabled={choices.length >= MAX_CHOICES}
            className={[
              "flex items-center gap-1 text-xs font-medium",
              choices.length >= MAX_CHOICES
                ? "text-slate-300 cursor-not-allowed"
                : "text-violet-700 hover:text-violet-800",
            ].join(" ")}
          >
            <AddIcon style={{ fontSize: 16 }} />
            Add answer
          </button>
        </div>
        <div className="flex flex-col gap-2">
          {choices.map((choice, idx) => {
            const isCorrect = !!choice.is_correct;
            return (
              <div
                key={idx}
                className={[
                  "flex items-center gap-2 rounded-lg border bg-white px-2 py-2",
                  isCorrect
                    ? "border-green-500 bg-green-50"
                    : "border-slate-200",
                ].join(" ")}
              >
                <button
                  type="button"
                  onClick={() =>
                    updateChoice(idx, { is_correct: !isCorrect })
                  }
                  aria-label={isCorrect ? "Mark incorrect" : "Mark correct"}
                  className={[
                    "flex-shrink-0 transition-colors",
                    isCorrect ? "text-green-600" : "text-slate-300 hover:text-slate-400",
                  ].join(" ")}
                >
                  {isCorrect ? (
                    <CheckCircleIcon style={{ fontSize: 22 }} />
                  ) : (
                    <RadioButtonUncheckedIcon style={{ fontSize: 22 }} />
                  )}
                </button>
                <input
                  type="text"
                  value={choice.text || ""}
                  onChange={(e) =>
                    updateChoice(idx, { text: e.target.value })
                  }
                  maxLength={CHOICE_MAX}
                  placeholder={`Answer ${idx + 1}`}
                  className="flex-1 px-2 py-1 text-sm bg-transparent focus:outline-none"
                />
                <span
                  className={[
                    "text-[11px] tabular-nums",
                    (choice.text || "").length > CHOICE_MAX
                      ? "text-red-600 font-semibold"
                      : "text-slate-400",
                  ].join(" ")}
                >
                  {(choice.text || "").length}/{CHOICE_MAX}
                </span>
                <IconButton
                  size="small"
                  onClick={() => removeChoice(idx)}
                  disabled={choices.length <= MIN_CHOICES}
                  aria-label="Remove answer"
                >
                  <CloseIcon style={{ fontSize: 16 }} />
                </IconButton>
              </div>
            );
          })}
        </div>
        {noCorrectAnswerWarning && (
          <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
            ⚠ Mark at least one answer correct, or no one will get this right.
          </div>
        )}
      </div>

      {/* Optional explanation */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
          Explanation (optional, shown after answering)
        </label>
        <textarea
          value={explanation}
          onChange={(e) => updateContent({ explanation: e.target.value })}
          rows={2}
          placeholder="Teach the answer when they get it wrong"
          className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-100 text-sm resize-none"
        />
      </div>

      {/* Shuffle toggle */}
      <label className="flex items-start gap-2 cursor-pointer">
        <Checkbox
          checked={shuffleChoices}
          onChange={(e) =>
            updateContent({ shuffle_choices: e.target.checked })
          }
          size="small"
          sx={{
            color: "#cbd5e1",
            "&.Mui-checked": { color: "#6d28d9" },
            p: 0,
            mt: "2px",
          }}
        />
        <div>
          <div className="text-sm font-medium text-slate-900">
            Shuffle answer order for each viewer
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Prevents employees from pattern-memorizing "the third one is right".
          </div>
        </div>
      </label>
    </div>
  );
}
